import React from 'react';
import { CheckCircle2, Globe2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCountriesFromFirebase } from '../../lib/firebase-service';

interface HeroSectionProps {
  onLogin: () => void;
  countriesCount: number;
}

// Fallback image that we know exists
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1539635278303-d4002c07eae3?q=80&w=2670&auto=format&fit=crop';

export function HeroSection({ onLogin, countriesCount }: HeroSectionProps) {
  const [isMobile, setIsMobile] = React.useState(false);
  const [heroImage, setHeroImage] = React.useState(FALLBACK_IMAGE);
  const [isImageLoading, setIsImageLoading] = React.useState(true);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Fetch random country for hero image
    const fetchRandomCountry = async () => {
      try {
        const countries = await getCountriesFromFirebase();
        if (countries && countries.length > 0) {
          const randomCountry = countries[Math.floor(Math.random() * countries.length)];
          
          // Pre-load the image
          const img = new Image();
          img.onload = () => {
            setHeroImage(randomCountry.image);
            setIsImageLoading(false);
          };
          img.onerror = () => {
            console.warn('Failed to load country image, using fallback');
            setHeroImage(FALLBACK_IMAGE);
            setIsImageLoading(false);
          };
          img.src = randomCountry.image;
        } else {
          setIsImageLoading(false);
        }
      } catch (error) {
        console.error('Error fetching random country:', error);
        setIsImageLoading(false);
      }
    };

    fetchRandomCountry();
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center">
      <div className="absolute inset-0">
        {/* Preload fallback image */}
        <link rel="preload" as="image" href={FALLBACK_IMAGE} />
        
        <img 
          src={heroImage}
          alt="Hero background"
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            isImageLoading ? 'opacity-0' : 'opacity-100'
          }`}
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-transparent" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="text-center lg:text-left lg:col-span-3">
            <h1 className="font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/80">
              <span className="block text-3xl sm:text-4xl md:text-5xl xl:text-6xl leading-tight">
                Organisez votre PVT
              </span>
              <span className="block text-2xl sm:text-3xl md:text-4xl xl:text-5xl mt-2 text-white/90">
                en toute simplicité
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Prépare ton PVT sans stress : checklist, budget, photos, PVTmate… tout est réuni dans une seule app pour t'accompagner à chaque étape de l'aventure !
            </p>

            {/* App Store & Google Play Badges - Updated to be larger with no hover effect */}
            <div className="mt-8 mb-8">
              <div className="flex flex-row gap-4 justify-center lg:justify-start items-center">
                <a 
                  href="https://apps.apple.com/ch/app/pvtistes-pvt-whv/id1151407622?l=fr-FR" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://pvtistes.net/wp-content/themes/pvtistest-2/static/ui/apple_store_badge.png"
                    alt="Download on the App Store" 
                    className="h-12 sm:h-16 w-auto"
                  />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.degordian.pvtistes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://pvtistes.net/wp-content/themes/pvtistest-2/static/ui/google-play-badge.png" 
                    alt="Disponible sur Google Play" 
                    className="h-12 sm:h-16 w-auto"
                  />
                </a>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-4 sm:gap-8 justify-center lg:justify-start text-sm sm:text-base">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary-500" />
                <span className="text-white/80">Gratuit</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-500" />
                <span className="text-white/80">500k+ membres</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe2 className="w-5 h-5 text-primary-500" />
                <span className="text-white/80">{countriesCount}+ pays</span>
              </div>
            </div>
          </div>
          
          <div className="hidden lg:block lg:col-span-2">
            {/* Empty right column for balance - just showing the background image */}
          </div>
        </div>
      </div>
    </section>
  );
}